import React, { useEffect, useMemo, useState } from "react";
import { teacherAPI } from "../services/api";
import "./ProfilePage.css";

const PERIOD_NUMBERS = [1, 2, 3, 4, 5, 6, 7, 8];

const toPeriodSelectionMap = (periods = []) => {
  const map = {};
  PERIOD_NUMBERS.forEach((periodNumber) => {
    map[periodNumber] = "";
  });
  (periods || []).forEach((row) => {
    const periodNumber = Number(row?.periodNumber);
    if (!PERIOD_NUMBERS.includes(periodNumber)) return;
    map[periodNumber] = row?.mode === "course" ? String(row?.courseId || "") : "";
  });
  return map;
};

export default function ProfilePage({ authProfile, onProfileUpdated }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [profileData, setProfileData] = useState(null);
  const [displayName, setDisplayName] = useState("");
  const [periodSelections, setPeriodSelections] = useState(() => toPeriodSelectionMap());

  const isTeacher = profileData?.role === "teacher";

  const courseOptions = useMemo(() => {
    return Array.isArray(profileData?.courseOptions) ? profileData.courseOptions : [];
  }, [profileData]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await teacherAPI.getProfileSettings();
        if (cancelled) return;
        const payload = res?.data || {};
        setProfileData(payload);
        setDisplayName(payload.displayName || "");
        setPeriodSelections(toPeriodSelectionMap(payload.periods || []));
      } catch (err) {
        if (cancelled) return;
        setError(err.response?.data?.error || "Could not load profile settings.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const handlePeriodChange = (periodNumber, value) => {
    setPeriodSelections((prev) => ({
      ...prev,
      [periodNumber]: String(value || ""),
    }));
    setSuccess("");
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const payload = {
        displayName: displayName,
      };

      if (isTeacher && profileData?.schoolId) {
        payload.schoolYear = profileData?.schoolYear || undefined;
        payload.periods = PERIOD_NUMBERS.map((periodNumber) => {
          const courseId = String(periodSelections[periodNumber] || "").trim();
          return {
            periodNumber,
            mode: courseId ? "course" : "prep",
            courseId: courseId || null,
          };
        });
      }

      const res = await teacherAPI.updateProfileSettings(payload);
      const updated = res?.data || {};
      setProfileData(updated);
      setDisplayName(updated.displayName || "");
      setPeriodSelections(toPeriodSelectionMap(updated.periods || []));
      setSuccess("Profile saved.");

      if (typeof onProfileUpdated === "function") {
        onProfileUpdated({
          userId: updated.userId,
          email: updated.email,
          displayName: updated.displayName,
          role: updated.role,
          isActive: updated.isActive,
        });
      }
    } catch (err) {
      setError(err.response?.data?.error || "Could not save profile.");
    } finally {
      setSaving(false);
    }
  };

  if (!authProfile) {
    return (
      <div className="panel-card">
        <p className="admin-error">Sign in to manage your profile.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="profile-page">
        <div className="panel-card">Loading profile…</div>
      </div>
    );
  }

  return (
    <div className="profile-page">
      <div className="panel-card profile-card">
        <div className="profile-card-head">
          <h3>Profile</h3>
          <p>Update how your name appears and keep your teaching schedule up to date.</p>
        </div>

        <div className="profile-grid">
          <label className="profile-field">
            <span>Display Name</span>
            <input
              type="text"
              value={displayName}
              onChange={(e) => {
                setDisplayName(e.target.value);
                setSuccess("");
              }}
              placeholder="How your name should appear"
              maxLength={120}
            />
          </label>

          <div className="profile-readonly">
            <span>Email</span>
            <strong>{profileData?.email || authProfile?.email || "—"}</strong>
          </div>
          <div className="profile-readonly">
            <span>Role</span>
            <strong>{profileData?.role || authProfile?.role || "teacher"}</strong>
          </div>
          <div className="profile-readonly">
            <span>School</span>
            <strong>{profileData?.schoolName || "Not assigned"}</strong>
          </div>
        </div>
      </div>

      {isTeacher && (
        <div className="panel-card profile-card">
          <div className="profile-card-head">
            <h3>Teaching Periods (1-8)</h3>
            <p>
              Pick the class taught during each period. Leave a period as
              <strong> Prep / PLC / Conference</strong> to exclude it.
            </p>
          </div>

          {!profileData?.schoolId ? (
            <p className="profile-warning">
              Your account is not assigned to a school yet, so class periods cannot be saved.
            </p>
          ) : null}

          <div className="profile-period-table-wrap">
            <table className="profile-period-table">
              <thead>
                <tr>
                  <th>Period</th>
                  <th>Class</th>
                </tr>
              </thead>
              <tbody>
                {PERIOD_NUMBERS.map((periodNumber) => {
                  const currentPeriod = (profileData?.periods || []).find(
                    (row) => Number(row?.periodNumber) === periodNumber,
                  );
                  return (
                    <tr key={periodNumber}>
                      <td>{`Period ${periodNumber}`}</td>
                      <td>
                        <select
                          value={periodSelections[periodNumber] || ""}
                          onChange={(e) => handlePeriodChange(periodNumber, e.target.value)}
                          disabled={!profileData?.schoolId}
                        >
                          <option value="">Prep / PLC / Conference</option>
                          {courseOptions.map((course) => (
                            <option key={course.id} value={course.id}>
                              {course.name}
                            </option>
                          ))}
                        </select>
                        {Number(currentPeriod?.conflictCount || 0) > 0 ? (
                          <span className="profile-conflict-note">
                            Multiple active courses found for this period. Saving will keep only the selected class.
                          </span>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="profile-note">
            School year: <strong>{profileData?.schoolYear || "current"}</strong>
          </p>
        </div>
      )}

      {(error || success) && (
        <div className="panel-card profile-feedback-card">
          {error ? <p className="profile-feedback profile-feedback-error">{error}</p> : null}
          {!error && success ? <p className="profile-feedback profile-feedback-success">{success}</p> : null}
        </div>
      )}

      <div className="profile-actions">
        <button
          type="button"
          className="tour-launch-btn"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? "Saving…" : "Save Profile"}
        </button>
      </div>
    </div>
  );
}
